import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type { ApiResponse } from '@/api/shared/types';
import {
  areaList,
  type AreaListItem,
  type AreaStatusFilter,
} from '@/api/domains/organization/areas';
import {
  positionCreate,
  positionDetail,
  positionUpdate,
  type CreatePositionRequest,
  type PositionDetail,
} from '@/api/domains/organization/positions';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useSidePanelLoading, useUI } from '@/hooks/ui';
import {
  createPositionSchema,
  type PositionFormData,
  updatePositionSchema,
} from '@/schemas/organization';
import { SECTIONS } from '@/types/sections';
import {
  formatToDdMmYyyy,
  injectValidationErrors,
  normalizeNumericSelectValue,
} from '@/utils';


type PositionFormMode = 'create' | 'edit';

export interface PositionFormRecord {
  mode?: PositionFormMode;
  positionId?: number;
}

interface UsePositionFormPanelParams {
  onSubmit?: () => void;
  readOnly?: boolean;
  record?: PositionFormRecord;
}

interface SelectorOption {
  value: string;
  label: string;
}

interface PositionFormMetadata {
  id: string;
  createdDate: string;
  createdBy: string;
  modifiedDate: string;
  modifiedBy: string;
}

export interface UsePositionFormPanelResult {
  codeValue: string;
  nameValue: string;
  descriptionValue: string;
  areaValue: string;
  areaOptions: SelectorOption[];
  metadata: PositionFormMetadata | null;
  isEditMode: boolean;
  effectiveReadOnly: boolean;
  isSubmitting: boolean;
  isReady: boolean;
  canSave: boolean;
  handleCodeChange: (value: string) => void;
  handleNameChange: (value: string) => void;
  handleDescriptionChange: (value: string) => void;
  handleAreaChange: (value: string | string[]) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
  errors: ReturnType<typeof useForm<PositionFormData>>['formState']['errors'];
  isSubmitted: boolean;
}

const MAX_PAGE_SIZE = 200;

async function loadAllAreas(): Promise<ApiResponse<AreaListItem[]>> {
  const allItems: AreaListItem[] = [];
  let currentPage = 1;

  while (true) {
    const response = await areaList.list({
      page: currentPage,
      pageSize: MAX_PAGE_SIZE,
      status: 'enabled' as AreaStatusFilter,
    });

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    allItems.push(...response.data.items);

    if (currentPage >= response.data.totalPages) {
      return {
        success: true,
        data: allItems,
      };
    }

    currentPage += 1;
  }
}

const mapMetadataFromDetail = (detail: PositionDetail): PositionFormMetadata => ({
  id: String(detail.id),
  createdDate: formatToDdMmYyyy(detail.createdDate) || '-',
  createdBy: detail.createdBy || '-',
  modifiedDate: formatToDdMmYyyy(detail.modifiedDate) || '-',
  modifiedBy: detail.modifiedBy || '-',
});

export function usePositionFormPanel(
  { onSubmit, readOnly, record }: UsePositionFormPanelParams,
): UsePositionFormPanelResult {
  const { t: translate } = useTranslation();
  const { canSection } = usePagePermissions();
  const { closeSidePanel, showNotification, showApiError } = useUI();
  const { setLoading } = useSidePanelLoading();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [allAreas, setAllAreas] = useState<AreaListItem[]>([]);
  const [metadata, setMetadata] = useState<PositionFormMetadata | null>(null);

  const latestRequestIdRef = useRef(0);
  const positionId = record?.positionId;
  const mode = record?.mode ?? (positionId ? 'edit' : 'create');
  const isEditMode = mode === 'edit';
  const effectiveReadOnly = !!readOnly;
  const canSave = isEditMode
    ? canSection(SECTIONS.CARGOS, 'edit')
    : canSection(SECTIONS.CARGOS, 'create');

  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    watch,
    setError,
    formState,
  } = useForm<PositionFormData>({
    resolver: zodResolver(isEditMode ? updatePositionSchema : createPositionSchema),
    defaultValues: {
      code: '',
      name: '',
      description: '',
      areaId: null,
    },
    mode: 'onSubmit',
  });

  const { errors, isSubmitted } = formState;

  const codeValue = watch('code') || '';
  const nameValue = watch('name') || '';
  const descriptionValue = watch('description') || '';
  const areaIdValue = watch('areaId');
  const areaValue = typeof areaIdValue === 'number' ? String(areaIdValue) : '';

  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      if (isEditMode && !positionId) {
        setIsBootstrapping(false);
        showNotification('error', translate('organization:messages.positionLoadError'));
        closeSidePanel();
        return;
      }

      setIsBootstrapping(true);
      setLoading(true);
      const requestId = ++latestRequestIdRef.current;

      const areasResponse = await loadAllAreas();

      if (!isMounted || requestId !== latestRequestIdRef.current) {
        return;
      }

      if (!areasResponse.success || !areasResponse.data) {
        showApiError(areasResponse, () => translate('organization:messages.areasLoadError'));
        closeSidePanel();
        setIsBootstrapping(false);
        setLoading(false);
        return;
      }

      setAllAreas(areasResponse.data);

      if (!isEditMode) {
        setIsBootstrapping(false);
        setLoading(false);
        return;
      }

      const currentPositionId = positionId as number;
      const detailResponse = await positionDetail.getById(currentPositionId);

      if (!isMounted || requestId !== latestRequestIdRef.current) {
        return;
      }

      if (!detailResponse.success || !detailResponse.data) {
        showApiError(detailResponse, () => translate('organization:messages.positionLoadError'));
        closeSidePanel();
        setIsBootstrapping(false);
        setLoading(false);
        return;
      }

      setValue('code', detailResponse.data.code, { shouldValidate: false });
      setValue('name', detailResponse.data.name, { shouldValidate: false });
      setValue('description', detailResponse.data.description, { shouldValidate: false });
      setValue('areaId', detailResponse.data.areaId, { shouldValidate: false });
      setMetadata(mapMetadataFromDetail(detailResponse.data));
      setIsBootstrapping(false);
      setLoading(false);
    };

    void bootstrap();

    return () => {
      isMounted = false;
      setLoading(false);
    };
  }, [
    closeSidePanel,
    isEditMode,
    positionId,
    setLoading,
    setValue,
    showApiError,
    showNotification,
    translate,
  ]);

  const areaOptions = useMemo(() => allAreas.map((area) => ({
    value: String(area.id),
    label: area.name,
  })), [allAreas]);

  const handleCodeChange = useCallback((value: string) => {
    setValue('code', value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [setValue]);

  const handleNameChange = useCallback((value: string) => {
    setValue('name', value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [setValue]);

  const handleDescriptionChange = useCallback((value: string) => {
    setValue('description', value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [setValue]);

  const handleAreaChange = useCallback((value: string | string[]) => {
    const normalized = normalizeNumericSelectValue(value) ?? null;

    setValue('areaId', normalized, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [setValue]);

  const isReady = useMemo(() => {
    const hasCode = codeValue.trim().length > 0;
    const hasName = nameValue.trim().length > 0;
    const hasDescription = descriptionValue.trim().length > 0;
    const hasArea = typeof areaIdValue === 'number' && areaIdValue > 0;

    return hasCode && hasName && hasDescription && hasArea && !isBootstrapping;
  }, [areaIdValue, codeValue, descriptionValue, isBootstrapping, nameValue]);

  const onValid = useCallback(async (data: PositionFormData) => {
    if (effectiveReadOnly || !canSave) {
      return;
    }

    setIsSubmitting(true);

    const requestPayload: CreatePositionRequest = {
      code: data.code.trim(),
      name: data.name.trim(),
      description: data.description.trim(),
      areaId: data.areaId ?? 0,
    };

    const response = isEditMode && positionId
      ? await positionUpdate.update(positionId, requestPayload)
      : await positionCreate.create(requestPayload);

    if (response.success && response.data) {
      showNotification(
        'success',
        translate(
          isEditMode
            ? 'organization:messages.positionUpdated'
            : 'organization:messages.positionCreated',
        ),
      );
      window.dispatchEvent(new CustomEvent('position-form-success'));
      onSubmit?.();
      closeSidePanel();
      setIsSubmitting(false);
      return;
    }

    if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
      injectValidationErrors(response.error.validationErrors, { setError });
      setIsSubmitting(false);
      return;
    }

    showApiError(response, translate(
      isEditMode
        ? 'organization:messages.positionUpdateError'
        : 'organization:messages.positionCreateError',
    ));
    setIsSubmitting(false);
  }, [
    canSave,
    closeSidePanel,
    effectiveReadOnly,
    isEditMode,
    onSubmit,
    positionId,
    setError,
    showApiError,
    showNotification,
    translate,
  ]);

  const onInvalid = useCallback(() => {
  }, []);

  const handleSubmit = useCallback((event?: FormEvent<HTMLFormElement>) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    void rhfHandleSubmit(onValid, onInvalid)(event);
  }, [onInvalid, onValid, rhfHandleSubmit]);

  return {
    codeValue,
    nameValue,
    descriptionValue,
    areaValue,
    areaOptions,
    metadata,
    isEditMode,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    canSave,
    handleCodeChange,
    handleNameChange,
    handleDescriptionChange,
    handleAreaChange,
    handleSubmit,
    errors,
    isSubmitted,
  };
}

export default usePositionFormPanel;